import React from "react";
import ReactDOM from "react-dom";
import {
  getContainerCharacterization,
  getContainerClientRegisteredInfo,
  getAzureBlobFile,
} from "../../../../services/container";
import { mergePdfs } from "../../../../components/download/pdfUtils";
import { formatOutletData, aggregateMaterialDelivery } from "../utils/utils";
import { repDescriptions } from "../../../../shared/utils/utils";
import { captureCharacterizationPdf } from "./capturePdf";
import { loadCharacterizationComments } from "./characterizationComments";
import { pdf } from "@react-pdf/renderer";
import { toast } from "react-toastify";
import { getSettingsData } from "../../../../services/settings";
import { ContainerViewMaterialForPDF } from "../../characterizationDetails/ContainerViewMaterialForPDF";
import {
  getEffectiveThresholdCm,
  getRegulationTypeFromContainer,
} from "../../characterizationDetails/constants/regulation";
import { mergeMaterialFluxes } from "./mergeMaterialFluxes";
import { sortObjectsInArrayByKeyName } from "../../../../components/utils/Object";

const fetchCharacterizationData = async (
  period,
  container,
  superContainer,
  i18n,
  t,
  characterizationType = "1199",
  sizeThreshold = 40,
) => {
  const containerId = superContainer ? superContainer._id : container?._id;
  if (!period || !containerId) {
    return null;
  }

  try {
    const requests = getContainerCharacterization(
      {
        periodMaterialStart: period.periodMaterialStartSubfilter,
        periodMaterialStop: period.periodMaterialStopSubfilter,
        customDate: !!period.periodMaterialStopSubfilter,
        language: i18n.language,
        characterizationType,
        sizeThreshold: getEffectiveThresholdCm(
          characterizationType,
          sizeThreshold,
        ),
      },
      containerId,
    );
    const requestArray = Array.isArray(requests) ? requests : [requests];
    const responses = await Promise.all(requestArray);
    const validResponses = responses.filter((r) => r?.status === 200);
    if (validResponses.length === 0) {
      return null;
    }
    const responseData = validResponses[0].data;

    const sortedHistory = (responseData.historyMaterial || []).sort(
      (a, b) =>
        new Date(b._time || 0).getTime() - new Date(a._time || 0).getTime(),
    );

    // Merge materials description
    const mergeConfig = JSON.parse(
      localStorage.getItem("materialsDescription") || "[]",
    ).map((v) => ({
      referenceName: v.referenceName,
      possibleNames: v.possibleNames,
    }));

    // Merge materialDelivery by cls and sizeRange
    const concatenatedMaterials = [
      ...(responseData.materialDelivery || [])
        .reduce((r, o) => {
          const sizeRange = o.sizeRange || "total";
          const key = `${o.cls}_${sizeRange}`;
          const item =
            r.get(key) ||
            Object.assign({}, o, {
              sizeRange,
              count: 0,
              massObject: 0,
              volumeObject: 0,
              finesMass: 0,
              finesVolume: 0,
              surface: 0,
              materialTotal: 0,
              totalMassRecoverable: 0,
              totalVolumeRecoverable: 0,
              recoverable: false,
            });
          item.count += +(o.count || 0);
          item.massObject += +(o.massObject || 0);
          item.volumeObject += +(o.volumeObject || 0);
          item.finesMass += +(o.finesMass || 0);
          item.finesVolume += +(o.finesVolume || 0);
          item.surface += +(o.surface || 0);
          item.materialTotal += +(o.materialTotal || 0);
          item.totalMassRecoverable += +(o.totalMassRecoverable || 0);
          item.totalVolumeRecoverable += +(o.totalVolumeRecoverable || 0);
          item.recoverable =
            o.recoverable !== undefined ? o.recoverable : item.recoverable;
          item.recoverableWithOutlet =
            o.data?.outletData?.recoverableWithOutlet;
          item.recoverableWithNoOutlet =
            o.data?.outletData?.recoverableWithNoOutlet;
          return r.set(key, item);
        }, new Map())
        .values(),
    ];

    const mergedFluxes = mergeMaterialFluxes(
      concatenatedMaterials,
      mergeConfig,
    );

    // Restore missing properties from concatenatedMaterials and include items not in mergeConfig
    const mergedFluxesClsSet = new Set(mergedFluxes.map((item) => item.cls));
    const itemsNotInMergeConfig = concatenatedMaterials.filter(
      (item) => !mergedFluxesClsSet.has(item.cls),
    );

    const mergedFluxesWithAllProps = mergedFluxes.map((mergedItem) => {
      const originalItem = concatenatedMaterials.find(
        (item) =>
          item.cls === mergedItem.cls &&
          item.sizeRange === mergedItem.sizeRange,
      );
      if (originalItem) {
        return {
          ...originalItem,
          ...mergedItem,
        };
      }
      return mergedItem;
    });

    // Combine merged items with items not in mergeConfig
    const allFluxes = [...mergedFluxesWithAllProps, ...itemsNotInMergeConfig];

    const sortedFluxes = sortObjectsInArrayByKeyName(allFluxes, "cls");
    const displayedMaterialDelivery = aggregateMaterialDelivery(sortedFluxes);

    const formattedOutletData = formatOutletData(
      responseData?.outletData,
      t,
      i18n.language,
    );

    let formattedRepData = {};
    if (characterizationType === "EPR" && responseData?.repData) {
      const newRepData = Array.isArray(responseData.repData)
        ? responseData.repData
        : [responseData.repData].filter(Boolean);
      if (newRepData.length > 0) {
        const mergedRep = newRepData.reduce(
          (acc, item) => {
            if (!item || !item.data) return acc;
            acc.volumeTotal += item.volumeTotal || 0;
            acc.massTotal += item.massTotal || 0;
            Object.entries(item.data).forEach(([rep, values]) => {
              const repKey = repDescriptions[rep]?.category || rep;
              if (!acc.data[repKey]) {
                acc.data[repKey] = {
                  massObject: 0,
                  volumeObject: 0,
                  objects: [],
                };
              }
              acc.data[repKey].massObject += values.massObject || 0;
              acc.data[repKey].volumeObject += values.volumeObject || 0;
              acc.data[repKey].objects = [
                ...new Set([
                  ...acc.data[repKey].objects,
                  ...(values.objects || []),
                ]),
              ];
            });
            return acc;
          },
          {
            volumeTotal: 0,
            massTotal: 0,
            data: {
              "PMCB Category 1": {
                massObject: 0,
                volumeObject: 0,
                objects: [],
              },
              "PMCB Category 2": {
                massObject: 0,
                volumeObject: 0,
                objects: [],
              },
              ABJ: {
                massObject: 0,
                volumeObject: 0,
                objects: [],
                description: t("Articles de bricolage et de jardin"),
              },
              ALS: {
                massObject: 0,
                volumeObject: 0,
                objects: [],
                description: t("Articles de loisirs et de sport"),
              },
              D3E: {
                massObject: 0,
                volumeObject: 0,
                objects: [],
                description: t(
                  "Responsabilité Élargie du Producteur pour les Déchets d'Équipements Électriques et Électroniques",
                ),
              },
            },
          },
        );

        const repChartLabels = Object.keys(mergedRep.data).sort();
        const repChartData = repChartLabels
          .filter((key) => mergedRep.data[key].volumeObject > 0)
          .map((key) => ({
            name: t(key),
            value: (
              (mergedRep.data[key].volumeObject / mergedRep.volumeTotal) *
              100
            ).toFixed(1),
          }));
        formattedRepData = {
          data: mergedRep.data,
          repChartData,
          repChartLabels: repChartLabels.map((key) => t(key)),
          volumeTotal: mergedRep.volumeTotal,
        };
      }
    }

    return {
      historyMaterial: sortedHistory,
      materialDelivery: displayedMaterialDelivery || [],
      parentCategoriesList: responseData.parentCategoriesList || [],
      recoverableMaterialList: responseData.recoverableMaterialList || [],
      totalWasteMass: responseData.totalWasteMass || 0,
      totalWasteVolume: responseData.totalWasteVolume || 0,
      totalMassRecoverable: responseData.totalMassRecoverable || 0,
      totalVolumeRecoverable: responseData.totalVolumeRecoverable || 0,
      materialTotal: responseData.materialTotal || 0,
      recoverableWithOutlet:
        responseData?.outletData?.recoverableWithOutlet?.massTotal || 0,
      recoverableWithNoOutlet:
        responseData?.outletData?.recoverableWithNoOutlet?.massTotal || 0,
      outletData: formattedOutletData,
      repData: formattedRepData,
      totalFinesMass: responseData.totalFinesMass || 0,
      totalFinesVolume: responseData.totalFinesVolume || 0,
      finesBelowThreshold: responseData.finesBelowThreshold || {
        count: 0,
        volumeObject: 0,
        massObject: 0,
      },
    };
  } catch (err) {
    console.error(
      "Erreur lors du chargement des données de caractérisation:",
      err,
    );
    return null;
  }
};

export const exportPdfFromRow = async ({
  period,
  container,
  superContainer,
  client,
  i18n,
  t,
  onProgress,
}) => {
  const reportProgress = (percent, labelKey) => {
    if (!onProgress) return;
    const label = labelKey ? t(labelKey) : "";
    onProgress({ percent, label });
  };

  const debug =
    process.env.REACT_APP_CHARACTERIZATION2_DEBUG === "true" ||
    process.env.REACT_APP_CHARACTERIZATION2_DEBUG === "1";
  try {
    reportProgress(10, "Loading...");
    const regulationType = getRegulationTypeFromContainer(container);

    // Get size threshold from settings
    let sizeThreshold = 40; // Default
    try {
      const settingsResponse = await getSettingsData();
      if (settingsResponse.data?.settings?.finesSizeThreshold !== undefined) {
        sizeThreshold = settingsResponse.data.settings.finesSizeThreshold;
      }
    } catch (error) {
      console.error("Error loading settings:", error);
    }

    // Fetch characterization data
    reportProgress(30, "Loading...");
    const data = await fetchCharacterizationData(
      period,
      container,
      superContainer,
      i18n,
      t,
      regulationType,
      sizeThreshold,
    );
    if (!data) {
      toast.error(t("No data available for export"));
      return;
    }

    // Create a hidden container for rendering components
    reportProgress(50, "Loading...");
    // Charts need to be visible (even if off-screen) to render properly
    const hiddenContainer = document.createElement("div");
    hiddenContainer.style.position = "fixed";
    hiddenContainer.style.left = "-9999px";
    hiddenContainer.style.top = "0";
    hiddenContainer.style.width = "1200px";
    hiddenContainer.style.height = "8000px";
    hiddenContainer.style.overflow = "auto";
    hiddenContainer.style.zIndex = "-9999";
    hiddenContainer.style.opacity = "1";
    hiddenContainer.style.pointerEvents = "none";
    document.body.appendChild(hiddenContainer);

    // Create refs for PDF capture
    const pdfMassChartRef = { current: null };
    const pdfMassTableRef = { current: null };
    const pdfOutletChartRef = { current: null };
    const pdfOutletTableRef = { current: null };
    const pdfVolumeChartRef = { current: null };
    const pdfObjectsChartRef = { current: null };
    const pdfRepChartRef = { current: null };
    const pdfRepTableRef = { current: null };

    // Render PDF-specific container with the same layout as detail view
    // We only render ContainerViewMaterialForPDF, which is designed for PDF capture.
    ReactDOM.render(
      <div
        style={{
          width: "1200px",
          minHeight: "8000px",
          padding: "20px",
          backgroundColor: "white",
        }}
      >
        <ContainerViewMaterialForPDF
          refs={{
            massChartRef: pdfMassChartRef,
            massTableRef: pdfMassTableRef,
            outletChartRef: pdfOutletChartRef,
            outletTableRef: pdfOutletTableRef,
            volumeChartRef: pdfVolumeChartRef,
            objectsChartRef: pdfObjectsChartRef,
            repChartRef: pdfRepChartRef,
            repTableRef: pdfRepTableRef,
          }}
          parentCategoriesList={data.parentCategoriesList}
          totalWasteMass={data.totalWasteMass}
          totalWasteVolume={data.totalWasteVolume}
          materialDelivery={data.materialDelivery}
          recoverableMaterialList={data.recoverableMaterialList}
          totalVolumeRecoverable={data.totalVolumeRecoverable}
          totalMassRecoverable={data.totalMassRecoverable}
          recoverableWithOutlet={data.recoverableWithOutlet}
          recoverableWithNoOutlet={data.recoverableWithNoOutlet}
          materialTotal={data.materialTotal}
          outletData={data.outletData}
          totalWeight={period?.weight ? period.weight : 0}
          repData={data.repData}
          characterizationType={regulationType}
          sizeThreshold={sizeThreshold}
          totalFinesMass={data.totalFinesMass}
          totalFinesVolume={data.totalFinesVolume}
          finesBelowThreshold={data.finesBelowThreshold}
          renderOffscreen={false}
        />
      </div>,
      hiddenContainer,
    );

    // Wait for components to render and charts to be ready
    // Check that refs are attached to DOM elements
    const waitForRefs = () => {
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 100; // 10 seconds max
        const checkRefs = () => {
          attempts++;
          const refsStatus = {
            massChart: !!pdfMassChartRef.current,
            massTable: !!pdfMassTableRef.current,
            volumeChart: !!pdfVolumeChartRef.current,
            objectsChart: !!pdfObjectsChartRef.current,
            outletChart: !!pdfOutletChartRef.current,
            outletTable: !!pdfOutletTableRef.current,
            repChart: !!pdfRepChartRef.current,
            repTable: !!pdfRepTableRef.current,
          };

          const allRefsAttached = Object.values(refsStatus).some(Boolean);

          if (debug) {
            // eslint-disable-next-line no-console
            console.log("Refs status:", refsStatus, "attempt:", attempts);
          }

          if (allRefsAttached || attempts >= maxAttempts) {
            // Additional wait for charts to fully render (charts need time to draw).
            // Augmenté pour être plus robuste lors de l'export depuis la liste.
            setTimeout(resolve, 8000);
          } else {
            setTimeout(checkRefs, 100);
          }
        };
        checkRefs();
      });
    };
    await waitForRefs();

    let registrationClientCode;
    let periodFiles = [];
    let pdfClient = client;
    try {
      const containerId = superContainer?._id ?? container?._id;
      const periodStart = period?.start != null ? new Date(period.start) : null;
      if (containerId && periodStart) {
        const clientResponse = await getContainerClientRegisteredInfo(
          {
            start: new Date(periodStart.getTime() - 1).toISOString(),
            stop: new Date(periodStart.getTime() + 1).toISOString(),
          },
          containerId,
        );
        if (clientResponse?.data?.client) {
          const clientData =
            typeof clientResponse.data.client === "string"
              ? JSON.parse(clientResponse.data.client)
              : clientResponse.data.client;
          registrationClientCode =
            clientData?.code != null ? String(clientData.code) : "";
          pdfClient = {
            ...(client || {}),
            ...clientData,
            name: clientData?.name || client?.name,
            address: clientData?.address || client?.address,
            clientUniqueId:
              clientData?.clientUniqueId || client?.clientUniqueId || "",
          };
          if (Array.isArray(clientData?.files)) {
            periodFiles = clientData.files;
          }
        } else if (
          clientResponse?.data &&
          Object.keys(clientResponse.data).length > 0
        ) {
          registrationClientCode = "";
        }
      }
    } catch (err) {
      console.warn("Could not load client registration for PDF:", err);
    }

    // Generate PDF
    reportProgress(80, "Loading...");
    const comments = await loadCharacterizationComments({
      container,
      superContainer,
      historyMaterial: data.historyMaterial,
    });
    const { pdfContent, pdfFilename } = await captureCharacterizationPdf({
      refs: {
        massChartRef: pdfMassChartRef,
        massTableRef: pdfMassTableRef,
        outletChartRef: pdfOutletChartRef,
        outletTableRef: pdfOutletTableRef,
        volumeChartRef: pdfVolumeChartRef,
        objectsChartRef: pdfObjectsChartRef,
        repChartRef: pdfRepChartRef,
        repTableRef: pdfRepTableRef,
      },
      period,
      superContainer,
      container,
      historyMaterial: data.historyMaterial,
      totalWasteMass: data.totalWasteMass,
      totalMassRecoverable: data.totalMassRecoverable,
      totalWasteVolume: data.totalWasteVolume,
      totalVolumeRecoverable: data.totalVolumeRecoverable,
      totalWeight: period?.weight ? period.weight : 0,
      client: pdfClient,
      registrationClientCode,
      wasteCategoryName: period?.waste_category || "-",
      parentCategoriesList: data.parentCategoriesList,
      outletData: data.outletData,
      comments,
      t,
      repData: data.repData,
      characterizationType: regulationType,
    });

    // Cleanup
    ReactDOM.unmountComponentAtNode(hiddenContainer);
    document.body.removeChild(hiddenContainer);

    // Generate PDF and append documents joints (like characterisation v1)
    reportProgress(95, "Loading...");
    const mainPdfBlob = await pdf(pdfContent).toBlob();
    const pdfList = [{ filename: pdfFilename, pdfBlob: mainPdfBlob }];

    if (periodFiles.length > 0) {
      const azureUrl = process.env.REACT_APP_AZURE_URL || "";
      const azureFiles = await Promise.all(
        periodFiles.map(async (filePath) => {
          const { data: fileBlob } = await getAzureBlobFile({
            blobUrl: `${azureUrl}${filePath}`,
          });
          const fileName = filePath.split("/").pop();
          return { filename: fileName, pdfBlob: fileBlob };
        }),
      );
      pdfList.push(...azureFiles);
      const mergedPdfBlob = await mergePdfs(pdfList);
      reportProgress(100, "Loading...");
      const url = URL.createObjectURL(mergedPdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = pdfFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      reportProgress(100, "Loading...");
      const url = URL.createObjectURL(mainPdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = pdfFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error("Error generating PDF:", error);
    console.error("Error details:", {
      message: error.message,
      stack: error.stack,
      container: container?._id,
      superContainer: superContainer?._id,
      period: period?._id,
    });
    toast.error(t("Error generating PDF"));
  }
};
